import {
  ConditionOperatorEnum,
  Provider,
  type InventoryResource,
} from '@repo/api-gw-sdk';

import { createCombineCondition } from '@/types/advanceFilter';

interface SourceConnection {
  // What the provider calls the connected scope a resource is discovered in.
  noun: string;
  route: string;
  // Pages whose account list can be filtered to one account from the link.
  filtersByAccount?: boolean;
}

const SOURCE_CONNECTIONS: Partial<Record<Provider, SourceConnection>> = {
  [Provider.Aws]: {
    noun: 'AWS account',
    route: '/settings/source-accounts/aws-accounts',
    filtersByAccount: true,
  },
  [Provider.Azure]: {
    noun: 'Azure subscription',
    route: '/settings/source-accounts/azure-subscriptions',
    filtersByAccount: true,
  },
  [Provider.Gcp]: {
    noun: 'GCP project',
    route: '/settings/source-accounts/gcp-projects',
    filtersByAccount: true,
  },
  [Provider.MongoAtlas]: {
    noun: 'MongoDB Atlas project',
    route: '/settings/source-accounts/mongo-atlas-projects',
  },
  [Provider.GoogleWorkspace]: {
    noun: 'Google Workspace',
    route: '/settings/source-accounts/saas-workspaces',
  },
  [Provider.Microsoft365]: {
    noun: 'Microsoft 365 tenant',
    route: '/settings/source-accounts/saas-workspaces',
  },
  [Provider.AzureEntraId]: {
    noun: 'Entra ID tenant',
    route: '/settings/source-accounts/entra-id',
  },
};

export const GENERIC_SOURCE_NOUN = 'source connection';

const sourceConnectionOf = (
  provider: Provider | undefined
): SourceConnection | undefined =>
  provider ? SOURCE_CONNECTIONS[provider] : undefined;

export const sourceNounOf = (provider: Provider | undefined) =>
  sourceConnectionOf(provider)?.noun ?? GENERIC_SOURCE_NOUN;

// Opens the resource's provider page under Source connections, narrowed to its own account where the page allows.
export const sourceConnectionHref = (
  resource: InventoryResource
): string | undefined => {
  const connection = sourceConnectionOf(resource.cloudProvider);
  if (!connection) {
    return undefined;
  }
  if (!connection.filtersByAccount || !resource.providerAccountId) {
    return connection.route;
  }
  const query = createCombineCondition([
    {
      property: 'providerAccountId',
      operator: ConditionOperatorEnum.In,
      value: [resource.providerAccountId],
    },
  ]);
  return `${connection.route}?query=${encodeURIComponent(JSON.stringify(query))}`;
};
